import { useState, useCallback, useEffect } from 'react';
import {
  Datagrid,
  FunctionField,
  ListContextProvider,
  Pagination,
  TextField,
  useList,
} from 'react-admin';
import { AdminSessionBalanceResponseDto, AdminSessionBalancesResponseDto } from 'shared/api';
import { API_BASE_URL_ADMIN } from 'shared/config';
import { LABELS } from 'shared/constants/labels';
import { httpClient } from 'shared/services/http-client';
import EntityLink from 'shared/components/EntityLink';

interface PromocodeListProps {
  url: string;
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
}

export const SessionBalanceList = ({ url, page, perPage, setPage, setPerPage }: PromocodeListProps) => {
  const [data, setData] = useState<AdminSessionBalancesResponseDto>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchSessionBalances = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await httpClient(API_BASE_URL_ADMIN + url, {
        method: 'GET',
      });
      const result = await response.json;

      setData(() => result);
    } catch (error) {
      console.error('Ошибка при загрузке балансов консультаций:', error);
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    fetchSessionBalances();
  }, [fetchSessionBalances]);

  const packagesWithId = data?.data || [];
  
  const baseContext = useList({ data: packagesWithId, page, perPage });
  const listContext = {
    ...baseContext,
    total: data?.total ?? 0,
  };

  if (isLoading && !data?.data?.length) return <div>Загрузка...</div>;

  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false}>
      <FunctionField
          label={LABELS.sessionBalanceId}
          render={(record: AdminSessionBalanceResponseDto) => (
            <EntityLink 
              record={record} 
              idField="id"
              basePath="/session-balances" 
              tooltipText="Перейти к балансу консультаций"
              labelField="id"
            />
          )}
        />
        <FunctionField
          label={LABELS.email}
          render={(record: AdminSessionBalanceResponseDto) => record.user.email}
        />
        <FunctionField
          label={LABELS.roleGroup}
          render={(record: AdminSessionBalanceResponseDto) => `${record.user.role || '-'}/${record.user.group || '-'}`}
        />
        <FunctionField
          label={LABELS.sessionBalanceUserLimit}
          render={() => '1'}
        />
        <FunctionField
          label={LABELS.sessionBalanceStatus}
          render={(record: AdminSessionBalanceResponseDto) => {
            switch (record.status) {
              case 'used':
                return 'Использовано';
              case 'unused':
                return 'Доступно';
              case 'expired':
                return 'Истек';
              case 'not_started':
                return 'Не доступно';
              default:
                return 'Неизвестно';
            }
          }}
        />
        <FunctionField
          label={LABELS.sessionBalanceLifeTime}
          render={(record: AdminSessionBalanceResponseDto) => {
            return `${new Date(record.startWith).toLocaleDateString()} - ${new Date(record.endWith).toLocaleDateString()}`;
          }}
        />
        <FunctionField
          label={LABELS.createdAt}
          render={(record: AdminSessionBalanceResponseDto) => {
            return new Date(record.createdAt).toLocaleDateString();
          }}
        />
        <TextField source="adminName" label={LABELS.sessionBalanceCreatorName} />
      </Datagrid>
      <Pagination
        page={page}
        perPage={perPage}
        setPage={setPage}
        setPerPage={setPerPage}
        total={listContext.total}
        isLoading={isLoading}
      />
    </ListContextProvider>
  );
};
